import React, {useEffect, useState} from 'react';

import {motion, useScroll, useTransform} from 'motion/react';
import {useIntl} from 'react-intl';

// YouTube Video Lightbox Component
const VideoLightbox: React.FC<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClose: () => void;
    videoId: string;
  }>
> = ({isOpen, onClose, videoId}) => {
  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      onClick={onClose}
    >
      {/* Close button */}
      <motion.button
        className="absolute top-4 right-4 z-60 text-white/80 hover:text-white transition-colors p-2"
        onClick={onClose}
        whileHover={{scale: 1.1}}
        whileTap={{scale: 0.9}}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          className="w-6 h-6"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </motion.button>

      {/* Video container */}
      <motion.div
        className="relative w-full max-w-4xl mx-4 lg:mx-8"
        initial={{scale: 0.8, opacity: 0}}
        animate={{scale: 1, opacity: 1}}
        exit={{scale: 0.8, opacity: 0}}
        transition={{type: 'spring', damping: 25, stiffness: 300}}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg shadow-2xl">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title="Stryd Video"
            className="absolute top-0 left-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

export const HeroSection: React.FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useIntl();
  const [isMobile, setIsMobile] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const {scrollY} = useScroll();

  // Subtle parallax for modern feel
  const backgroundY = useTransform(scrollY, [0, 800], [0, -100]);
  const contentY = useTransform(scrollY, [0, 600], [0, -30]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden max-w-[100vw]">
      {/* Subtle Gradient Background - Oura inspired */}
      <motion.div
        className="absolute inset-0 w-full h-full"
        style={{y: backgroundY}}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-amber-100 via-orange-100 to-orange-200" />
        <div className="absolute inset-0 bg-gradient-to-tr from-neutral-50/80 via-amber-100/70 to-orange-100/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-50/60 to-amber-100/40" />

        {/* Enhanced texture overlay */}
        <div className="absolute inset-0 opacity-[0.15] bg-[radial-gradient(circle_at_50%_50%,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[length:32px_32px]" />
      </motion.div>

      {/* Main Content Container */}
      <motion.div
        className="relative z-10 w-full max-w-7xl mx-auto px-4 lg:px-4"
        style={{y: contentY}}
      >
        <div className="grid lg:grid-cols-[35%_65%] gap-8 lg:gap-12 items-center min-h-[80vh]">
          {/* Left Side - Text Content */}
          <motion.div
            className="space-y-8 lg:space-y-12 text-center lg:text-left order-2 lg:order-1"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.8, delay: 0.2}}
          >
            {/* Product Label */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white text-sm font-medium rounded-full"
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.3}}
            >
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              {intl.formatMessage({id: 'home.hero.productLabel'})}
            </motion.div>

            {/* Main Headlines - Four Rows */}
            <div className="space-y-2 lg:space-y-4">
              <motion.h1
                className="text-2xl md:text-3xl lg:text-4xl xl:text-6xl font-light text-neutral-900 leading-[0.9]"
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.4}}
              >
                {intl.formatMessage({id: 'home.hero.mainHeadline'})}
              </motion.h1>

              <motion.h4
                className="text-2xl md:text-3xl lg:text-4xl xl:text-6xl font-light leading-[0.9]"
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.7}}
              >
                <span className="bg-gradient-to-r from-orange-500 via-orange-400 to-amber-400 bg-clip-text text-transparent font-medium">
                  {intl.formatMessage({id: 'home.hero.gradientHeadline'})}
                </span>
              </motion.h4>
            </div>

            {/* Subtitle */}
            <motion.p
              className="text-lg lg:text-xl text-neutral-600 max-w-lg mx-auto lg:mx-0 leading-relaxed font-light"
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.8}}
            >
              {intl.formatMessage({id: 'home.hero.subtitle'})}
            </motion.p>

            {/* CTA Button */}
            <motion.div
              className="pt-4"
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.9}}
            >
              <motion.a
                className="group relative inline-flex items-center gap-3 px-8 py-4 m-1 bg-neutral-900 text-white font-medium rounded-full text-lg transition-all duration-300 hover:bg-neutral-800 hover:scale-105 hover:shadow-xl"
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                href="/store"
              >
                {intl.formatMessage({id: 'home.hero.shopButton'})}
                <motion.div
                  className="w-5 h-5 border-2 border-white/60 rounded-full flex items-center justify-center"
                  animate={{rotate: [0, 360]}}
                  transition={{duration: 8, repeat: Infinity, ease: 'linear'}}
                >
                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                </motion.div>
              </motion.a>
              <motion.button
                className="group relative inline-flex items-center gap-3 px-8 py-4 mt-4 m-1 bg-neutral-900 text-white font-medium rounded-full text-lg transition-all duration-300 hover:bg-neutral-800 hover:scale-105 hover:shadow-xl cursor-pointer"
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                onClick={() => setIsVideoOpen(true)}
              >
                {intl.formatMessage({id: 'home.hero.watchButton'})}
                {/* <motion.div
                  className="w-5 h-5 border-2 border-white/60 rounded-full flex items-center justify-center"
                  animate={{rotate: [0, 360]}}
                  transition={{duration: 8, repeat: Infinity, ease: 'linear'}}
                >
                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                </motion.div> */}
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Right Side - Product Images */}
          <motion.div
            className="relative flex justify-center lg:justify-end items-center order-1 lg:order-2"
            initial={{opacity: 0, scale: 0.8}}
            animate={{opacity: 1, scale: 1}}
            transition={{duration: 1, delay: 0.5, ease: 'easeOut'}}
          >
            <div className="relative flex items-center justify-center max-w-5xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl">
              {/* Phone Screen - Background */}
              <motion.div
                className="relative max-w-[180px] lg:max-w-[200px] xl:max-w-[250px]"
                style={{
                  top: '-20%',
                  right: '-15%',
                }}
                animate={{
                  y: [0, -6, 0],
                  rotateY: [0, -1, 0],
                }}
                transition={{
                  duration: 9,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 1,
                }}
              >
                <img
                  src="/images/adaptive-training/home-page-at.png"
                  alt="Adaptive Training Interface"
                  className="w-full h-auto object-contain drop-shadow-xl relative z-[5]"
                  style={{
                    filter:
                      'drop-shadow(0 0 12px rgba(59, 130, 246, 0.15)) drop-shadow(0 6px 24px rgba(0, 0, 0, 0.12))',
                  }}
                />

                {/* Phone glow effects - subtle */}
                <motion.div
                  className="absolute inset-0 bg-gradient-radial from-blue-300/30 via-indigo-200/20 to-transparent rounded-2xl blur-xl scale-105"
                  animate={{
                    opacity: [0.2, 0.4, 0.2],
                    scale: [1.05, 1.15, 1.05],
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 2,
                  }}
                />
              </motion.div>

              {/* Stryd Pod - Foreground/Highlight */}
              <motion.div
                className="relative max-w-[380px] lg:max-w-[480px] xl:max-w-[580px] -ml-12 lg:-ml-16 xl:-ml-20"
                animate={{
                  y: [0, -12, 0],
                  rotateY: [0, 3, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <img
                  src="/images/stryd5/Still 2B.png"
                  alt="Stryd 5.0 Pod"
                  className="w-full h-auto object-contain drop-shadow-2xl relative z-20"
                  style={{
                    filter:
                      'drop-shadow(0 0 25px rgba(251, 146, 60, 0.4)) drop-shadow(0 12px 48px rgba(0, 0, 0, 0.25))',
                  }}
                />

                {/* Enhanced Pod glow effects */}
                <motion.div
                  className="absolute inset-0 bg-gradient-radial from-orange-300/70 via-amber-200/50 to-transparent rounded-full blur-3xl scale-125"
                  animate={{
                    opacity: [0.7, 1, 0.7],
                    scale: [1.2, 1.6, 1.2],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />

                <motion.div
                  className="absolute inset-0 bg-gradient-radial from-orange-400/50 via-orange-300/25 to-transparent rounded-full blur-2xl scale-110"
                  animate={{
                    opacity: [0.5, 0.8, 0.5],
                    scale: [1.1, 1.4, 1.1],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 1,
                  }}
                />

                {/* Additional pod accent glow */}
                <motion.div
                  className="absolute inset-0 bg-gradient-radial from-amber-300/40 via-orange-300/20 to-transparent rounded-full blur-xl scale-100"
                  animate={{
                    opacity: [0.3, 0.6, 0.3],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.5,
                  }}
                />
              </motion.div>
            </div>

            {/* Enhanced floating accent elements - focused on pod as hero */}
            <motion.div
              className="absolute -top-16 -right-8 w-28 h-28 bg-gradient-to-br from-orange-400/70 to-amber-400/60 rounded-full blur-2xl"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.7, 1, 0.7],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5,
              }}
            />

            <motion.div
              className="absolute -bottom-12 -left-4 w-24 h-24 bg-gradient-to-br from-amber-400/60 to-orange-300/50 rounded-full blur-xl"
              animate={{
                scale: [1, 1.6, 1],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.5,
              }}
            />

            {/* Pod highlight accent lights */}
            <motion.div
              className="absolute top-1/5 -left-8 w-20 h-20 bg-gradient-to-br from-orange-300/50 to-amber-300/40 rounded-full blur-lg"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.5, 0.9, 0.5],
                x: [-6, 6, -6],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.8,
              }}
            />

            <motion.div
              className="absolute bottom-1/4 -right-10 w-16 h-16 bg-gradient-to-br from-amber-400/60 to-orange-400/50 rounded-full blur-lg"
              animate={{
                scale: [1, 1.4, 1],
                opacity: [0.6, 1, 0.6],
                y: [-4, 4, -4],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.2,
              }}
            />

            {/* Subtle phone accent for balance */}
            <motion.div
              className="absolute top-1/2 -right-20 w-14 h-14 bg-gradient-to-br from-blue-400/20 to-indigo-300/15 rounded-full blur-md"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.4, 0.2],
                x: [2, -2, 2],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2.5,
              }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20"
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        transition={{duration: 0.8, delay: 1.5}}
      >
        <motion.div
          className="flex flex-col items-center gap-3"
          animate={{y: [0, 6, 0]}}
          transition={{duration: 2.5, repeat: Infinity, ease: 'easeInOut'}}
        >
          {/* Scroll text */}
          <motion.span
            className="text-sm font-medium text-neutral-700 tracking-wider uppercase"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.8, delay: 2}}
          >
            {intl.formatMessage({id: 'home.hero.scrollIndicator'})}
          </motion.span>

          {/* Mouse indicator */}
          <motion.div
            className="w-6 h-10 border-2 border-neutral-600/60 rounded-full flex justify-center bg-white/80 backdrop-blur-sm shadow-lg"
            whileHover={{scale: 1.1}}
          >
            <motion.div
              className="w-1.5 h-3 bg-neutral-700 rounded-full mt-2"
              animate={{
                opacity: [0.5, 1, 0.5],
                y: [0, 3, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </motion.div>

          {/* Downward arrow */}
          <motion.div
            className="flex flex-col items-center gap-1"
            animate={{
              y: [0, 4, 0],
              opacity: [0.6, 1, 0.6],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.5,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              className="text-neutral-600"
            >
              <path
                d="M7 10L12 15L17 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              className="text-neutral-500"
            >
              <path
                d="M7 10L12 15L17 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Video Lightbox */}
      <VideoLightbox
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoId="9MHzNIyUfLU"
      />
    </section>
  );
};
